import { isRecord } from '../../../../../utils/typeGuards';

export { isRecord };

export function normalizeRequiredId(value: unknown, errorLabel: string): string {
    if (typeof value !== 'string') {
        throw new Error(`Expected "${errorLabel}" to be a string.`);
    }

    const trimmed = value.trim();
    if (trimmed.length === 0) {
        throw new Error(`Expected "${errorLabel}" to be non-empty.`);
    }

    return trimmed;
}

export function normalizeRequiredPositiveIntegerId(value: unknown, errorLabel: string): string {
    if (typeof value !== 'string') {
        throw new Error(`Expected "${errorLabel}" to be a string.`);
    }

    const normalized = value.trim();
    if (/^[1-9]\d*$/.test(normalized)) {
        return normalized;
    }

    throw new Error(`Expected "${errorLabel}" to be a positive integer.`);
}

export function toPositiveIntegerIdNumber(value: string, errorLabel: string): number {
    return Number.parseInt(normalizeRequiredPositiveIntegerId(value, errorLabel), 10);
}

export function normalizeString(value: unknown, errorLabel: string): string {
    if (typeof value !== 'string') {
        throw new Error(`Expected "${errorLabel}" to be a string.`);
    }

    return value;
}

export function normalizeNonEmptyString(value: unknown, errorLabel: string): string {
    const normalizedValue = normalizeString(value, errorLabel).trim();
    if (!normalizedValue.length) {
        throw new Error(`Expected "${errorLabel}" to be non-empty.`);
    }

    return normalizedValue;
}

export function normalizeRequiredFiniteNumber(value: unknown, errorLabel: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`Expected "${errorLabel}" to be a finite number.`);
    }

    return value;
}

export function normalizeOrderedTimeWindow(
    startValue: unknown,
    endValue: unknown,
    errorLabelPrefix: string
): { startTime: number; endTime: number } {
    const startTime = normalizeRequiredFiniteNumber(startValue, `${errorLabelPrefix}.startTime`);
    const endTime = normalizeRequiredFiniteNumber(endValue, `${errorLabelPrefix}.endTime`);

    if (startTime >= endTime) {
        throw new Error(
            `Expected "${errorLabelPrefix}.startTime" to be lower than "${errorLabelPrefix}.endTime".`
        );
    }

    return { startTime, endTime };
}

export function normalizeRequiredInteger(value: unknown, errorLabel: string): number {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new Error(`Expected "${errorLabel}" to be an integer.`);
    }

    return value;
}

export function normalizeOptionalPositiveInteger(
    value: unknown,
    errorLabel: string
): number | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
        throw new Error(`Expected "${errorLabel}" to be a positive integer when provided.`);
    }

    return value;
}

export function parseOptionalPositiveIntegerArg(
    value: string | null,
    errorLabel: string
): number | undefined {
    if (value === null) {
        return undefined;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error(`Expected "${errorLabel}" to be a positive integer.`);
    }

    return parsed;
}

function normalizeNullableValue<T>(
    value: unknown,
    errorLabel: string,
    guard: (candidate: unknown) => candidate is T,
    expectedType: string
): T | null {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    if (!guard(value)) {
        throw new Error(`Expected "${errorLabel}" to be ${expectedType} or null.`);
    }

    return value;
}

export function normalizeNullableInteger(value: unknown, errorLabel: string): number | null {
    return normalizeNullableValue(
        value,
        errorLabel,
        (candidate): candidate is number =>
            typeof candidate === 'number' && Number.isInteger(candidate),
        'an integer'
    );
}

export function normalizeNullableFiniteNumber(value: unknown, errorLabel: string): number | null {
    return normalizeNullableValue(
        value,
        errorLabel,
        (candidate): candidate is number =>
            typeof candidate === 'number' && Number.isFinite(candidate),
        'a finite number'
    );
}

export function normalizeNullableString(value: unknown, errorLabel: string): string | null {
    return normalizeNullableValue(
        value,
        errorLabel,
        (candidate): candidate is string => typeof candidate === 'string',
        'a string'
    );
}

export function normalizeNullableBoolean(value: unknown, errorLabel: string): boolean | null {
    return normalizeNullableValue(
        value,
        errorLabel,
        (candidate): candidate is boolean => typeof candidate === 'boolean',
        'a boolean'
    );
}

export function normalizeNullableRecord(
    value: unknown,
    errorLabel: string
): Record<string, unknown> | null {
    return normalizeNullableValue(value, errorLabel, isRecord, 'an object');
}

export function normalizeRequiredRecord(
    value: unknown,
    errorLabel: string
): Record<string, unknown> {
    if (isRecord(value)) {
        return value;
    }

    throw new Error(`Expected "${errorLabel}" to be an object.`);
}

export function normalizeArrayEntries<T>(
    value: unknown,
    pathLabel: string,
    normalizeEntry: (entry: unknown, entryPath: string) => T,
    options?: { allowNullish?: boolean; expectedType?: string }
): T[] {
    const allowNullish = options?.allowNullish ?? false;
    const expectedType = options?.expectedType ?? 'an array';

    if (value === null || typeof value === 'undefined') {
        if (allowNullish) {
            return [];
        }

        throw new Error(`Expected "${pathLabel}" to be ${expectedType}.`);
    }

    if (!Array.isArray(value)) {
        throw new Error(`Expected "${pathLabel}" to be ${expectedType}.`);
    }

    return value.map((entry, index) => normalizeEntry(entry, `${pathLabel}[${index}]`));
}
